/**
 * Synthetic match + mini vision mixer for grading tests and bot balancing.
 *
 * This is NOT the real football sim — it's a cheap, deterministic stand-in
 * that emits plausible phases, events (with `ideal` hints) and per-camera
 * CameraFrame semantics, so grading can be exercised end to end without the
 * renderer or src/sim. `runSyntheticMatch` wires sim → booth → bot → mixer →
 * grader at 30Hz and returns the report.
 */
import type {
  CameraFrame, CameraKind, CommentatorRequest, DirectorState, GradeFeedback, GradeReport,
  GraphicKind, MatchConfig, MatchEvent, MatchEventType, MatchPhase, PlayerProfile, Rng,
  ShotSize, TeamProfile, TeamSide, Vec3, WorldSnapshot, PlayerState,
} from '../core/contracts';
import { createRng } from '../core/rng';
import { createBooth, type Booth } from './booth';
import type { BotAction, BotInput } from './bot';
import { camKindFromKit } from './cameras';
import { createGrader, type Difficulty, type SportsGrader } from './grader';

// ---------------------------------------------------------------------------
// Camera kit (ids match render/cameras/kit.ts)
// ---------------------------------------------------------------------------

export const SYNTHETIC_KIT: readonly { id: string; kind: CameraKind; position: Vec3 }[] = [
  { id: 'CAM 1', kind: 'main_wide', position: { x: 0, y: 22, z: -58 } },
  { id: 'CAM 2', kind: 'main_tight', position: { x: 3.5, y: 22, z: -58 } },
  { id: 'CAM 3', kind: 'high_18_left', position: { x: -36, y: 18, z: -55 } },
  { id: 'CAM 4', kind: 'high_18_right', position: { x: 36, y: 18, z: -55 } },
  { id: 'CAM 5', kind: 'behind_goal_left', position: { x: -66, y: 7, z: -3 } },
  { id: 'CAM 6', kind: 'behind_goal_right', position: { x: 66, y: 7, z: -3 } },
  { id: 'CAM 7', kind: 'pitchside_handheld_left', position: { x: -22, y: 1.6, z: -36.5 } },
  { id: 'CAM 8', kind: 'pitchside_handheld_right', position: { x: 22, y: 1.6, z: -36.5 } },
  { id: 'CAM 9', kind: 'reverse_angle', position: { x: 0, y: 20, z: 60 } },
  { id: 'BENCH', kind: 'bench', position: { x: 0, y: 1.7, z: -35.2 } },
  { id: 'CROWD', kind: 'crowd', position: { x: 14, y: 2.2, z: -37 } },
];
export const syntheticCamKindOf = camKindFromKit(SYNTHETIC_KIT);

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

const SURNAMES = ['OKAFOR', 'MARSH', 'DIALLO', 'KOWALSKI', 'BRENNAN', 'SILVA', 'HARTLEY', 'NAKAMURA', 'OSEI', 'FINCH',
  'DUARTE', 'LINDQVIST', 'MORETTI', 'ADEYEMI', 'CLARKE', 'VOSS', 'IBRAHIM', 'PRICE', 'RUIZ', 'HOLM', 'BAKER', 'NOVAK',
  'QUINN', 'MENSAH', 'ROWE', 'TANAKA', 'FERREIRA', 'WALSH'];
const POS: PlayerProfile['position'][] = ['GK', 'DEF', 'DEF', 'DEF', 'DEF', 'MID', 'MID', 'MID', 'FWD', 'FWD', 'FWD'];

function makeTeam(rng: Rng, side: TeamSide, name: string, short: string, manager: string): TeamProfile {
  const mk = (i: number, bench: boolean): PlayerProfile => {
    const sn = SURNAMES[(rng.int(0, SURNAMES.length - 1) + i) % SURNAMES.length]!;
    const number = bench ? 12 + i : i + 1;
    return {
      id: `${side}-${number}`, number, name: `${sn.charAt(0)}. ${sn.charAt(0)}${sn.slice(1).toLowerCase()}`, shortName: sn,
      position: bench ? 'MID' : POS[i]!, rating: rng.int(55, 85),
      stats: { seasonGoals: rng.int(0, 15), seasonAssists: rng.int(0, 10), appearances: rng.int(5, 30), age: rng.int(18, 34), nationality: 'ENG' },
    };
  };
  return {
    id: short.toLowerCase(), side, name, shortName: short, primaryColor: '#c8102e', secondaryColor: '#ffffff', keeperColor: '#222222',
    formation: '4-3-3', players: Array.from({ length: 11 }, (_, i) => mk(i, false)), bench: Array.from({ length: 3 }, (_, i) => mk(i, true)),
    manager, form: ['W', 'D', 'L'], leaguePosition: rng.int(1, 20),
  };
}

export function syntheticConfig(seed: number, halfLengthSeconds = 150, drama = 0.6, competition = 'Regional League — Matchday 1'): MatchConfig {
  const rng = createRng(seed ^ 0xc0f1);
  return {
    seed, competition, venue: 'Synthetic Park', weather: 'clear', timeOfDay: 'night', halfLengthSeconds, drama,
    home: makeTeam(rng, 'home', 'Northbridge Rovers', 'NBR', 'Frank Doyle'),
    away: makeTeam(rng, 'away', 'Eastport Athletic', 'EPA', 'Marta Lindgren'),
  };
}

// ---------------------------------------------------------------------------
// Synthetic match
// ---------------------------------------------------------------------------

export interface SyntheticMatch {
  readonly config: MatchConfig;
  step(dt: number): MatchEvent[];
  snapshot(): WorldSnapshot;
  frames(): CameraFrame[];
  readonly finished: boolean;
  readonly events: readonly MatchEvent[];
}

type Outcome = 'goal' | 'save' | 'wide' | 'near_miss' | 'corner' | 'foul';
interface Attack { side: TeamSide; dir: 1 | -1; start: number; end: number; fromX: number; z: number; outcome: Outcome }
interface Scheduled { at: number; fn: () => void }

const v = (x: number, y: number, z: number): Vec3 => ({ x, y, z });
const d2 = (a: Vec3, b: Vec3) => Math.hypot(a.x - b.x, a.z - b.z);

export function createSyntheticMatch(config: MatchConfig): SyntheticMatch {
  const rng = createRng(config.seed);
  const half = config.halfLengthSeconds;
  const drama = config.drama;
  let t = 0, halfStart = 0, halfNo: 1 | 2 = 1;
  let phase: MatchPhase = 'pre_match';
  let phaseUntil = 6;
  let afterPhase: MatchPhase = 'kickoff';
  const score = { home: 0, away: 0 };
  let ball = v(0, 0.11, 0);
  let wanderTarget = v(0, 0.11, 0);
  let focus = v(0, 0.11, 0);
  let intensity = 0.05;
  let possession: TeamSide | null = 'home';
  let attack: Attack | null = null;
  let nextAttackAt = 14, nextPassAt = 9, nextFoulAt = 60;
  let subsDone = 0;
  let interest: { id: string; pos: Vec3; until: number } | null = null;
  let managerTeam: TeamSide = 'home';
  let finished = false;
  let eid = 1;
  const events: MatchEvent[] = [];
  let out: MatchEvent[] = [];
  let sched: Scheduled[] = [];

  const players: PlayerState[] = [];
  const baseX = [-48, -30, -30, -30, -30, -12, -12, -12, 5, 5, 5];
  const baseZ = [0, -22, -8, 8, 22, -18, 0, 18, -20, 0, 20];
  for (const side of ['home', 'away'] as const) {
    const team = side === 'home' ? config.home : config.away;
    for (let i = 0; i < 11; i++) {
      const s = side === 'home' ? 1 : -1;
      players.push({ id: team.players[i]!.id, team: side, number: i + 1, pos: v(baseX[i]! * s, 0, baseZ[i]!), vel: v(0, 0, 0), heading: 0, anim: 'idle', animPhase: 0, hasBall: false });
    }
  }
  const pById = (id: string) => players.find((p) => p.id === id);
  const teamPlayers = (side: TeamSide) => players.filter((p) => p.team === side);
  const keeper = (side: TeamSide) => teamPlayers(side)[0]!;
  const forward = (side: TeamSide) => rng.pick(teamPlayers(side).slice(5));
  const nearestToBall = () => {
    let best = players[0]!, bd = Infinity;
    for (const p of players) { const dd = d2(p.pos, ball); if (dd < bd) { bd = dd; best = p; } }
    return best;
  };
  const clock = () => (halfNo - 1) * 2700 + Math.min(1, (t - halfStart) / half) * 2700;

  function emit(type: MatchEventType, importance: number, o: {
    team?: TeamSide | null; playerId?: string | null; secondary?: string | null; pos?: Vec3;
    window?: number; sizes?: ShotSize[]; subject?: string | 'ball' | null; replay?: boolean; graphics?: GraphicKind[]; text?: string;
  } = {}): MatchEvent {
    const ev: MatchEvent = {
      id: eid++, t, matchClock: clock(), type, team: o.team ?? null, playerId: o.playerId ?? null,
      secondaryPlayerId: o.secondary ?? null, pos: o.pos ?? { ...ball }, importance,
      ideal: {
        windowSeconds: o.window ?? 2, shotSizes: o.sizes ?? ['wide', 'extreme_wide'], subjectId: o.subject === undefined ? 'ball' : o.subject,
        replayWorthy: o.replay ?? false, ...(o.graphics ? { graphics: o.graphics } : {}),
      },
      text: o.text ?? type.replace(/_/g, ' '),
    };
    events.push(ev); out.push(ev);
    if (ev.playerId) {
      const p = pById(ev.playerId);
      interest = { id: ev.playerId, pos: p ? { ...p.pos } : v(0, 0, -33), until: t + 15 };
    }
    return ev;
  }
  const at = (dtFrom: number, fn: () => void) => sched.push({ at: t + dtFrom, fn });
  const setPhase = (p: MatchPhase, dur: number, then: MatchPhase) => { phase = p; phaseUntil = t + dur; afterPhase = then; };
  const shortName = (id: string) => [...config.home.players, ...config.away.players, ...config.home.bench, ...config.away.bench].find((p) => p.id === id)?.shortName ?? id;

  function stoppage(dur: number, pos: Vec3, then: MatchPhase = 'open_play') {
    setPhase('stoppage', dur, then);
    focus = { ...pos };
    attack = null;
    // Second-half substitutions happen at stoppages.
    if (halfNo === 2 && subsDone < 2 && t - halfStart > half * (0.25 + 0.3 * subsDone)) {
      subsDone++;
      const side: TeamSide = rng.chance(0.5) ? 'home' : 'away';
      const team = side === 'home' ? config.home : config.away;
      const incoming = team.bench[subsDone - 1]!;
      phaseUntil += 6;
      at(1.5, () => {
        emit('substitution', 0.3, { team: side, playerId: incoming.id, pos: v(0, 0, -33), window: 6, sizes: ['medium', 'close'], subject: incoming.id, graphics: ['sub_lower_third'], text: `${incoming.shortName} comes on` });
        interest = { id: incoming.id, pos: v(0, 0, -33), until: t + 12 };
      });
    }
  }

  function startAttack() {
    const side: TeamSide = rng.chance(0.5) ? 'home' : 'away';
    const dir: 1 | -1 = (side === 'home') === (halfNo === 1) ? 1 : -1;
    const pGoal = 0.08 + 0.12 * drama;
    const r = rng.next();
    const outcome: Outcome = r < pGoal ? 'goal' : r < pGoal + 0.22 ? 'save' : r < pGoal + 0.44 ? 'wide'
      : r < pGoal + 0.54 ? 'near_miss' : r < pGoal + 0.72 ? 'corner' : 'foul';
    attack = { side, dir, start: t, end: t + rng.range(6, 10), fromX: ball.x, z: rng.range(-20, 20), outcome };
    possession = side;
  }

  function resolveAttack(a: Attack) {
    const atk = a.side, def: TeamSide = atk === 'home' ? 'away' : 'home';
    const shooter = forward(atk);
    const goalPos = v(52.5 * a.dir, 0, 0);
    const name = shooter.id;
    switch (a.outcome) {
      case 'goal': {
        score[atk]++;
        emit('goal', 1, { team: atk, playerId: name, pos: { ...ball }, window: 2.5, sizes: ['wide', 'medium'], replay: true, graphics: ['goal_lower_third'], text: `GOAL! ${shortName(name)} scores` });
        ball = { ...goalPos, y: 0.5 };
        setPhase('goal_celebration', 20, 'kickoff');
        const scorer = pById(name)!;
        scorer.pos = v(40 * a.dir, 0, -30);
        focus = { ...scorer.pos };
        managerTeam = def;
        at(1.2, () => emit('celebration', 0.8, { team: atk, playerId: name, pos: { ...scorer.pos }, window: 8, sizes: ['close', 'medium'], subject: name, text: `${shortName(name)} celebrates` }));
        at(7, () => emit('crowd_moment', 0.35, { team: atk, pos: v(0, 5, 45), window: 4, sizes: ['wide', 'extreme_wide'], subject: null }));
        at(11, () => emit('manager_reaction', 0.45, { team: def, pos: v(0, 0, -35), window: 5, sizes: ['close', 'medium'], subject: `manager-${def}` }));
        break;
      }
      case 'save': {
        emit('shot_on_target', 0.55, { team: atk, playerId: name, window: 2, sizes: ['wide', 'medium'], replay: false });
        at(0.4, () => {
          const gk = keeper(def);
          emit('save', 0.7, { team: def, playerId: gk.id, secondary: name, window: 2.5, sizes: ['wide', 'medium'], replay: true, text: `Great save` });
          if (rng.chance(0.5)) {
            emit('corner', 0.3, { team: atk, pos: v(52.5 * a.dir, 0, 34), window: 3 });
            setPhase('set_piece', 8, 'open_play'); intensity = 0.35; attack = null;
          } else stoppage(6, { ...ball });
        });
        break;
      }
      case 'wide':
        emit('shot', 0.45, { team: atk, playerId: name, window: 2.5, sizes: ['wide', 'medium'], replay: true, text: `${shortName(name)} fires wide` });
        at(0.6, () => { emit('goal_kick', 0.1, { team: def }); stoppage(6, { ...ball }); });
        break;
      case 'near_miss':
        emit('near_miss', 0.62, { team: atk, playerId: name, window: 2.5, sizes: ['wide', 'medium'], replay: true, text: `${shortName(name)} so close!` });
        at(0.6, () => stoppage(7, { ...ball }));
        break;
      case 'corner':
        emit('corner', 0.3, { team: atk, pos: v(52.5 * a.dir, 0, 34), window: 3 });
        setPhase('set_piece', 8, 'open_play'); intensity = 0.35; attack = null;
        break;
      case 'foul': {
        const fouler = rng.pick(teamPlayers(def).slice(1, 8));
        const ev = emit('foul', 0.45, { team: def, playerId: fouler.id, secondary: name, window: 3, sizes: ['wide', 'medium'], replay: true, text: `Foul by ${shortName(fouler.id)}` });
        const injury = rng.chance(0.15);
        stoppage(injury ? 24 : 13, { ...ev.pos }, 'set_piece');
        afterPhase = 'set_piece';
        if (rng.chance(0.45)) {
          at(2.5, () => emit('yellow_card', 0.65, { team: def, playerId: fouler.id, pos: { ...fouler.pos }, window: 6, sizes: ['close', 'medium'], subject: fouler.id, graphics: ['card_lower_third'], text: `Yellow for ${shortName(fouler.id)}` }));
        }
        if (injury) at(1.5, () => emit('injury', 0.5, { team: atk, playerId: name, window: 6, sizes: ['close', 'medium'], subject: name }));
        if (rng.chance(0.5)) { managerTeam = def; at(6, () => emit('manager_reaction', 0.4, { team: def, pos: v(0, 0, -35), window: 4, sizes: ['close', 'medium'], subject: `manager-${def}` })); }
        break;
      }
    }
  }

  function endPhase() {
    const next = afterPhase;
    if (phase === 'half_time') {
      halfNo = 2; halfStart = t; ball = v(0, 0.11, 0);
      emit('kickoff', 0.2, { window: 3 });
      setPhase('kickoff', 2, 'open_play');
      return;
    }
    if (next === 'kickoff') {
      ball = v(0, 0.11, 0); focus = { ...ball };
      emit('kickoff', 0.2, { window: 3 });
      setPhase('kickoff', 2, 'open_play');
    } else if (next === 'set_piece') {
      emit('free_kick', 0.3, { pos: { ...ball }, window: 3 });
      setPhase('set_piece', 5, 'open_play'); intensity = 0.4;
    } else {
      phase = next; phaseUntil = Infinity;
    }
    nextPassAt = t + 1;
    nextAttackAt = Math.max(nextAttackAt, t + rng.range(8, 16));
  }

  function moveWorld(dt: number) {
    const live = phase === 'open_play' || phase === 'kickoff';
    if (live && attack) {
      const p = Math.min(1, (t - attack.start) / (attack.end - attack.start));
      const tx = attack.fromX + (attack.dir * 40 - attack.fromX) * p;
      ball = v(tx, 0.11, attack.z * (1 - p * 0.6) + Math.sin(t * 0.8) * 3);
      intensity = 0.3 + 0.6 * p;
    } else if (live) {
      if (d2(ball, wanderTarget) < 2) wanderTarget = v(rng.range(-25, 25), 0.11, rng.range(-25, 25));
      const dd = d2(ball, wanderTarget) || 1;
      const sp = 6 * dt;
      ball = v(ball.x + ((wanderTarget.x - ball.x) / dd) * sp, 0.11, ball.z + ((wanderTarget.z - ball.z) / dd) * sp);
      intensity += (0.2 + 0.08 * Math.sin(t * 0.3) - intensity) * (1 - Math.exp(-dt / 2));
    } else if (phase === 'set_piece') {
      intensity += (0.35 - intensity) * (1 - Math.exp(-dt / 2));
    } else {
      intensity += (0.08 - intensity) * (1 - Math.exp(-dt / 1.5));
    }
    if (live || phase === 'set_piece') focus = { ...ball };
    // Players drift toward their formation spot shifted toward the ball.
    for (let i = 0; i < players.length; i++) {
      const p = players[i]!;
      if (phase === 'goal_celebration' && interest && p.id === interest.id) continue;
      const bi = i % 11, s = p.team === 'home' ? 1 : -1;
      const tx = baseX[bi]! * s * 0.6 + ball.x * 0.45, tz = baseZ[bi]! * 0.8 + ball.z * 0.3;
      const k = 1 - Math.exp(-dt / 1.2);
      p.pos = v(p.pos.x + (tx - p.pos.x) * k, 0, p.pos.z + (tz - p.pos.z) * k);
    }
    const carrier = nearestToBall();
    for (const p of players) p.hasBall = live && p === carrier;
    if (interest) {
      const ip = pById(interest.id);
      if (ip) interest.pos = { ...ip.pos };
    }
  }

  function frameFor(cam: (typeof SYNTHETIC_KIT)[number]): CameraFrame {
    const live = phase === 'open_play' || phase === 'kickoff' || phase === 'set_piece';
    const carrier = nearestToBall();
    const subj = !live && interest && interest.until > t ? { id: interest.id, pos: interest.pos } : { id: carrier.id, pos: carrier.pos };
    const near = (c: Vec3, r: number) => players.filter((p) => d2(p.pos, c) < r).map((p) => p.id);
    let size: ShotSize = 'wide', subjectId: string | 'ball' | null = 'ball', ballIn = true, focusIn = true;
    let visible: string[] = [], target: Vec3 = { ...focus }, fov = 30, quality = 0.85;
    const zone = (ok: boolean, sz: ShotSize) => { size = sz; ballIn = ok; focusIn = ok; visible = ok ? [...near(focus, 25), 'ball'] : []; };
    switch (cam.kind) {
      case 'main_wide': case 'reverse_angle':
        size = 'wide'; visible = [...near(focus, 30), 'ball']; fov = 30; ballIn = d2(ball, focus) < 40; break;
      case 'main_tight':
        size = 'medium'; subjectId = carrier.id; target = { ...carrier.pos };
        ballIn = d2(ball, carrier.pos) < 4; focusIn = d2(focus, carrier.pos) < 8; visible = [...near(carrier.pos, 6), ...(ballIn ? ['ball'] : [])]; fov = 10; break;
      case 'high_18_left': zone(focus.x < -5, 'wide'); break;
      case 'high_18_right': zone(focus.x > 5, 'wide'); break;
      case 'behind_goal_left': zone(focus.x < -28, 'medium'); fov = 20; break;
      case 'behind_goal_right': zone(focus.x > 28, 'medium'); fov = 20; break;
      case 'pitchside_handheld_left': case 'pitchside_handheld_right': {
        const mine = (cam.kind === 'pitchside_handheld_left') === (subj.pos.x < 0);
        size = mine ? 'close' : 'medium'; subjectId = subj.id; target = { ...subj.pos };
        ballIn = d2(ball, subj.pos) < 3; focusIn = d2(focus, subj.pos) < 5; visible = [subj.id, ...(ballIn ? ['ball'] : [])];
        fov = mine ? 8 : 16; quality = mine ? 0.9 : 0.7; break;
      }
      case 'bench':
        size = 'close'; subjectId = `manager-${managerTeam}`; ballIn = false; focusIn = false;
        visible = ['manager-home', 'manager-away']; target = v(0, 1, -38); fov = 12; break;
      case 'crowd':
        size = 'wide'; subjectId = null; ballIn = false; focusIn = false; visible = []; target = v(0, 8, 50); fov = 25; break;
      default: break;
    }
    return { camId: cam.id, position: { ...cam.position }, target, fov, framing: { subjectId, shotSize: size, ballInFrame: ballIn, focusInFrame: focusIn, visible, quality } };
  }

  return {
    config,
    get finished() { return finished; },
    events,
    step(dt) {
      out = [];
      if (finished) return out;
      t += dt;
      const due = sched.filter((s) => s.at <= t);
      sched = sched.filter((s) => s.at > t);
      for (const s of due) s.fn();
      if (t >= phaseUntil) endPhase();
      const live = phase === 'open_play';
      if (live && t - halfStart >= half && !attack) {
        if (halfNo === 1) { emit('half_time', 0.3, { window: 4, subject: null }); setPhase('half_time', 8, 'kickoff'); }
        else { emit('full_time', 0.3, { window: 4, subject: null }); phase = 'full_time'; phaseUntil = Infinity; finished = true; }
      } else if (live) {
        if (!attack && t >= nextAttackAt) startAttack();
        if (attack && t >= attack.end) { const a = attack; attack = null; resolveAttack(a); nextAttackAt = t + rng.range(16, 32) / (0.6 + drama); }
        else if (!attack && t >= nextFoulAt) {
          nextFoulAt = t + rng.range(50, 90);
          const side: TeamSide = rng.chance(0.5) ? 'home' : 'away';
          const f = rng.pick(teamPlayers(side).slice(1, 8));
          emit('foul', 0.3, { team: side, playerId: f.id, window: 2.5, sizes: ['wide', 'medium'] });
          stoppage(9, { ...ball }, 'set_piece');
        } else if (t >= nextPassAt && phase === 'open_play') {
          nextPassAt = t + rng.range(1.5, 3);
          emit('pass', 0.05, { team: possession, playerId: nearestToBall().id, window: 2 });
          if (rng.chance(0.25)) possession = possession === 'home' ? 'away' : 'home';
        }
      }
      moveWorld(dt);
      return out;
    },
    snapshot() {
      return {
        t, matchClock: clock(), half: halfNo, phase, score: { ...score }, possession,
        ball: { pos: { ...ball }, vel: v(0, 0, 0), spin: 0, ownerId: nearestToBall().id },
        players: players.map((p) => ({ ...p, pos: { ...p.pos } })),
        officials: { referee: { ...ball }, assistant1: v(0, 0, -34), assistant2: v(0, 0, 34) },
        focus: { ...focus }, intensity,
      };
    },
    frames: () => SYNTHETIC_KIT.map(frameFor),
  };
}

// ---------------------------------------------------------------------------
// Mini vision mixer (mirrors director/mixer.ts semantics closely enough)
// ---------------------------------------------------------------------------

export interface MiniMixer {
  state: DirectorState;
  apply(a: BotAction, t: number, events: readonly MatchEvent[]): void;
  update(dt: number, t: number): void;
}

export function createMiniMixer(initialCam = 'CAM 1'): MiniMixer {
  const state: DirectorState = {
    programCamId: initialCam, previewCamId: 'CAM 2', cuts: [],
    replay: { active: false, fromT: 0, toT: 0, headT: 0, speed: 1, camId: initialCam, eventId: null },
    graphics: [], graphicsLog: [], instructions: {},
  };
  let gid = 1;
  let keyT: number | null = null;
  const pickAngle = (ev: MatchEvent | null): string => {
    if (!ev) return 'CAM 2';
    if (['goal', 'shot', 'shot_on_target', 'save', 'near_miss', 'woodwork'].includes(ev.type)) return ev.pos.x >= 0 ? 'CAM 4' : 'CAM 3';
    return 'CAM 2';
  };
  const hide = (pred: (kind: GraphicKind) => boolean, t: number) => {
    state.graphics = state.graphics.filter((g) => {
      if (!pred(g.kind)) return true;
      g.hiddenAt = t;
      return false;
    });
  };
  const endReplay = (t: number) => {
    if (!state.replay.active) return;
    state.replay.active = false;
    state.cuts.push({ t, camId: state.programCamId, transition: 'stinger', isReplay: false });
  };
  return {
    state,
    apply(a, t, events) {
      switch (a.type) {
        case 'hot_punch':
          if (state.replay.active || a.camId === state.programCamId) return;
          state.programCamId = a.camId;
          state.cuts.push({ t, camId: a.camId, transition: 'cut', isReplay: false });
          return;
        case 'replay_roll': {
          const ev = a.eventId != null
            ? events.find((e) => e.id === a.eventId) ?? null
            : [...events].reverse().find((e) => e.ideal.replayWorthy && t - e.t < 40) ?? null;
          const fromT = ev ? ev.t - 4 : t - 6;
          const toT = ev ? Math.min(t, ev.t + 2.5) : t;
          if (toT - fromT < 0.5) return;
          const camId = pickAngle(ev);
          keyT = ev ? ev.t : null;
          Object.assign(state.replay, { active: true, fromT, toT, headT: fromT, speed: 1, camId, eventId: ev ? ev.id : null });
          state.cuts.push({ t, camId, transition: 'stinger', isReplay: true });
          return;
        }
        case 'replay_angle':
          if (!state.replay.active || a.camId === state.replay.camId) return;
          state.replay.camId = a.camId;
          state.cuts.push({ t, camId: a.camId, transition: 'cut', isReplay: true });
          return;
        case 'replay_end': endReplay(t); return;
        case 'graphic_show': {
          hide((k) => k === a.kind, t);
          const g = { id: gid++, kind: a.kind, shownAt: t, ...(a.subjectId ? { subjectId: a.subjectId } : {}) };
          state.graphics.push(g);
          state.graphicsLog.push(g);
          return;
        }
        case 'graphic_hide': hide((k) => k === a.target, t); return;
      }
    },
    update(dt, t) {
      const r = state.replay;
      if (!r.active) return;
      r.speed = keyT !== null && Math.abs(r.headT - keyT) < 1 ? 0.5 : 1;
      r.headT += dt * r.speed;
      if (r.headT >= r.toT) endReplay(t);
    },
  };
}

// ---------------------------------------------------------------------------
// End-to-end runner
// ---------------------------------------------------------------------------

export type DirectorFn = (input: BotInput) => BotAction[];

export interface RunOptions {
  seed: number;
  halfLengthSeconds?: number;
  drama?: number;
  difficulty?: Difficulty;
  /** Director policy (e.g. `bot.decide`). Omit for a director who never touches anything. */
  director?: DirectorFn;
  /** Disable the booth. */
  noBooth?: boolean;
  dt?: number;
}

export interface RunResult {
  report: GradeReport;
  feedback: GradeFeedback[];
  requests: readonly CommentatorRequest[];
  grader: SportsGrader;
  booth: Booth;
  director: DirectorState;
  events: readonly MatchEvent[];
}

export function runSyntheticMatch(o: RunOptions): RunResult {
  const dt = o.dt ?? 1 / 30;
  const config = syntheticConfig(o.seed, o.halfLengthSeconds ?? 150, o.drama ?? 0.6);
  const match = createSyntheticMatch(config);
  const booth = createBooth(config, createRng((o.seed ^ 0xb007) >>> 0), { difficulty: o.difficulty ?? 'pro' });
  const grader = createGrader(config, {
    difficulty: o.difficulty ?? 'pro', camKindOf: syntheticCamKindOf,
    onBoothResolved: (id, st) => booth.resolve(id, st),
  });
  const mixer = createMiniMixer();
  const feedback: GradeFeedback[] = [];
  let guard = 0;
  while (!match.finished && guard++ < 1_000_000) {
    const newEvents = match.step(dt);
    const snap = match.snapshot();
    if (!o.noBooth) booth.update(snap, newEvents, snap.t);
    const requests = o.noBooth ? [] : booth.open(snap.t);
    const frames = match.frames();
    if (o.director) {
      for (const a of o.director({ snapshot: snap, newEvents, frames, requests: booth.active(snap.t), director: mixer.state })) {
        mixer.apply(a, snap.t, match.events);
      }
    }
    mixer.update(dt, snap.t);
    const d = mixer.state;
    const programFrame = d.replay.active ? null : frames.find((f) => f.camId === d.programCamId) ?? null;
    feedback.push(...grader.tick({ dt, snapshot: snap, newEvents, director: d, programFrame, allFrames: frames, requests }));
  }
  return { report: grader.report(), feedback, requests: booth.all(), grader, booth, director: mixer.state, events: match.events };
}
